const { deleteRadarUser } = require('../api/radarProfile');
const { markTutorialAsSeen } = require('../api/tutorials');
const {
  clearTutorialsRead,
  saveReadTutorialId,
  tutorialsReadOfflineSelector,
  tutorialsReadByUserSelector,
} = require('../slices/tutorials');
const userApi = require('../api/users');
const { authSelector } = require('../selectors');
const {
  fetchPurchasesIfNeeded,
  invalidatePurchases,
} = require('./PurchaseActions');
const {
  fetchCardsIfNeeded,
  invalidateCards,
} = require('../slices/cards/actions');
const { getProfileFromToken } = require('../utils/tokenparser');
const {
  MONTHLY_STORE_DISCOUNT_UPDATED,
  UPDATE_USER_DATA,
} = require('../reducers/auth');
const {
  updateLootcoinBalance,
  updateLootgemBalance,
  updateTicketBalance,
} = require('./helpers');
const { legalDataSelector, userSelector } = require('../selectors');
const { setLegalDataConsent } = require('../api/legal');
const { APPROVE_LEGAL_DATA, CONSUME_LEGAL_DATA } = require('../reducers/legal');

// todo should be moved to config
const MAX_NUMBER_FAVOURITES = 50;

const MIN_USER_UPDATE = 1000 * 60 * 5; // 5 minutes
const FETCH_USER_TIMEOUT = 1000 * 30; // 30 seconds

function updateUserData(data) {
  return {
    type: UPDATE_USER_DATA,
    data,
  };
}

function fetchUserInProgress() {
  return {
    type: 'FETCH_USER_IN_PROGRESS',
    timestamp: Date.now(),
  };
}

function fetchUserSuccessful(user) {
  return (dispatch) => {
    dispatch({
      type: 'FETCH_USER_SUCCESSFUL',
      user,
      timestamp: Date.now(),
    });
  };
}

function fetchUserFailed(reason) {
  return {
    type: 'FETCH_USER_FAILED',
    reason,
    timestamp: Date.now(),
  };
}

function updateUserSuccessful(updatedUser) {
  return {
    type: 'UPDATE_USER_SUCCESSFUL',
    updatedUser,
    timestamp: Date.now(),
  };
}

function setLanguage(language) {
  return {
    type: 'SET_LANGUAGE',
    language,
    timestamp: Date.now(),
  };
}

const consumeLegalData = () => ({
  type: CONSUME_LEGAL_DATA,
});

function sendApproveLegalData() {
  return async (dispatch, getState) => {
    const state = getState();
    const {
      auth: { apiUrl, token },
    } = state;
    const { _id, consentedAt = new Date() } = legalDataSelector(state);

    const consent = {
      legalData: _id,
      consentedAt,
    };

    if (token) {
      setLegalDataConsent(apiUrl, token, consent).then(() => {
        const { consents } = userSelector(state);
        dispatch(updateUserData({ consents: [...consents, consent] }));
        dispatch(consumeLegalData());
      });
    }
  };
}

function approveLegalData() {
  return async (dispatch) => {
    dispatch({ type: APPROVE_LEGAL_DATA });
    return dispatch(sendApproveLegalData());
  };
}

function subscribeToNewsletter(newsletterEmail, captchaToken) {
  return async (dispatch, getState) => {
    const state = getState();
    const {
      auth: { apiUrl, token },
    } = state;

    return userApi
      .subscribeToNewsletter(apiUrl, token, newsletterEmail, captchaToken)
      .then(
        (newsletterInfo) => {
          if (!token) {
            return newsletterInfo;
          }
          const {
            newsletterEmail: updatedNewsletterEmail,
            newLootcoinBalance: lootcoinBalance,
            newLootgemBalance: lootgemBalance,
            newTicketBalance: ticketBalance,
          } = newsletterInfo;
          dispatch(
            updateUserData({
              newsletterEmail: updatedNewsletterEmail || undefined,
              lootcoinBalance:
                typeof lootcoinBalance === 'number'
                  ? lootcoinBalance
                  : undefined,
              lootgemBalance:
                typeof lootgemBalance === 'number' ? lootgemBalance : undefined,
              ticketBalance:
                typeof ticketBalance === 'number' ? ticketBalance : undefined,
            }),
          );
          return newsletterInfo;
        },
        (error) => {
          throw error;
        },
      );
  };
}

// todo: this shouldn't be here but it's not worth refactoring as this is addressed in new lib
const readTutorial = (tutorialId) => async (dispatch, getState) => {
  const state = getState();
  const {
    auth: { apiUrl, token },
  } = state;
  const tutorialsReadByUser = tutorialsReadByUserSelector(state);
  const tutorialsReadOffline = tutorialsReadOfflineSelector(state);

  if (token) {
    if (!tutorialsReadByUser[tutorialId]) {
      markTutorialAsSeen(apiUrl, token, tutorialId).then(() => {
        const { tutorialsRead } = userSelector(state);
        dispatch(
          updateUserData({ tutorialsRead: [...tutorialsRead, tutorialId] }),
        );
      });
    }
  } else if (!tutorialsReadOffline[tutorialId]) {
    dispatch(saveReadTutorialId(tutorialId));
  }
};

function updateSeenTutorials() {
  return (dispatch, getState) => {
    const tutorialsRead = tutorialsReadOfflineSelector(getState());
    Object.keys(tutorialsRead).forEach((tutorialId) =>
      dispatch(readTutorial(tutorialId)),
    );
    dispatch(clearTutorialsRead());
  };
}

function fetchUser() {
  return (dispatch, getState) => {
    dispatch(fetchUserInProgress());

    const state = getState();
    const { apiUrl, token } = state.auth;
    if (!token) {
      return Promise.reject(new Error('Need to be logged in to fetch user'));
    }
    const profile = getProfileFromToken(token);
    const userId = profile && profile.user;

    return userApi.fetchUser(apiUrl, token, userId).then(
      (user) => {
        if (user.deleted || user.disabled) {
          dispatch({
            type: 'LOGOUT_SUCCESSFUL',
            timestamp: Date.now(),
          });
          return user;
        }
        dispatch(fetchUserSuccessful(user));
        const { consentedAt, _id, consentConsumed } = legalDataSelector(state);
        const { consents } = user;
        if (consentedAt && !consentConsumed) {
          if (!consents.some(({ legalData }) => _id === legalData)) {
            dispatch(sendApproveLegalData());
          } else {
            dispatch(consumeLegalData());
          }
        }
        dispatch(fetchCardsIfNeeded());
        dispatch(fetchPurchasesIfNeeded());
        dispatch(updateSeenTutorials());
        return user;
      },
      (error) => {
        dispatch(fetchUserFailed(error));
        throw error;
      },
    );
  };
}

function shouldFetchUser(state, skipOutdatedCheck) {
  // We can only fetch the user if we have a token
  if (!state.auth.token) {
    return false;
  }

  const { fetchingInProgressSince, didInvalidate, lastSuccessfulFetch } =
    state.auth.user;

  if (!fetchingInProgressSince) {
    // Should update if it was explicetely invalidated
    if (didInvalidate) {
      return true;
    }

    // Should update if data is too old
    if (
      !skipOutdatedCheck &&
      (!lastSuccessfulFetch ||
        Date.now() - lastSuccessfulFetch > MIN_USER_UPDATE)
    ) {
      return true;
    }
  } else if (
    fetchingInProgressSince &&
    Date.now() - fetchingInProgressSince > FETCH_USER_TIMEOUT
  ) {
    // Ensure we cannot get stuck in fetchingInProgressSince=true state.
    return true;
  }

  return false;
}

function fetchUserIfNeeded(require) {
  return (dispatch, getState) => {
    const state = getState();
    if (require || shouldFetchUser(state)) {
      return dispatch(fetchUser());
    }
    return Promise.resolve(userSelector(state));
  };
}

function invalidateUser() {
  return {
    type: 'INVALIDATE_USER',
    timestamp: Date.now(),
  };
}

function updateUser(updatedUser) {
  return (dispatch, getState) => {
    const state = getState();
    const { apiUrl, token } = state.auth;
    if (!token) {
      return Promise.reject(new Error('Need to be logged in to update user'));
    }
    const profile = getProfileFromToken(token);
    const userId = profile && profile.user;
    return userApi
      .updateUser(apiUrl, token, userId, updatedUser)
      .then((responseJson) => {
        dispatch(updateUserSuccessful(responseJson));
        return responseJson;
      });
  };
}

function markHintAsSeen(hint) {
  return (dispatch, getState) => {
    const state = getState();
    const { apiUrl, token } = state.auth;
    const user = state.auth.user.data;
    if (!token || !user) {
      return Promise.reject(new Error('Need to be logged in to update user'));
    }
    const profile = getProfileFromToken(token);
    const userId = profile && profile.user;
    if (user.seenHints && user.seenHints.includes(hint)) {
      return Promise.resolve();
    }
    const seenHints = user.seenHints ? [...user.seenHints, hint] : [hint];
    return userApi
      .updateUser(apiUrl, token, userId, { seenHints })
      .then((responseJson) => {
        dispatch(updateUserSuccessful(responseJson));
      });
  };
}

function setPasswordSuccesful() {
  return {
    type: 'SET_PASSWORD_SUCCESSFUL',
  };
}

function updateTotalSubscriptionDiamondBonus({
  totalSubscriptionDiamondBonus,
  subscriptionDiamondBonusToBeClaimed,
}) {
  return {
    type: 'TOTAL_SUBSCRIPTION_DIAMOND_BONUS_UPDATED',
    subscriptionDiamondBonusToBeClaimed,
    totalSubscriptionDiamondBonus,
  };
}

function setPassword(currentPassword, newPassword) {
  return (dispatch, getState) => {
    const state = getState();
    const { apiUrl, token } = state.auth;
    if (!token) {
      return Promise.reject(new Error('Need to be logged in to set password'));
    }
    const profile = getProfileFromToken(token);
    const userId = profile && profile.user;
    return userApi
      .setPassword(apiUrl, token, userId, currentPassword, newPassword)
      .then(() => {
        dispatch(setPasswordSuccesful());
      });
  };
}

function deleteAccount() {
  return (dispatch, getState) => {
    const state = getState();
    const { apiUrl, token } = state.auth;
    if (!token) {
      return Promise.reject(
        new Error('Need to be logged in to delete your account'),
      );
    }
    return userApi.deleteUser(apiUrl, token).then(() => {
      deleteRadarUser(token)
        // eslint-disable-next-line no-console
        .catch((e) => console.log(e))
        .finally(() => {
          dispatch({
            type: 'LOGOUT_SUCCESSFUL',
            timestamp: Date.now(),
          });
        });
    });
  };
}

function verifyEmail(emailVerificationToken) {
  return (dispatch, getState) => {
    const { apiUrl, token } = getState().auth;

    return userApi
      .verifyEmail(apiUrl, token, emailVerificationToken)
      .then((response) => {
        dispatch({
          type: 'LOGIN_SUCCESSFUL',
          token: response.token,
          timestamp: Date.now(),
        });
        return dispatch(fetchUser());
      });
  };
}

function requestEmailVerification() {
  return (dispatch, getState) => {
    const { apiUrl, token } = getState().auth;

    return userApi.requestEmailVerification(apiUrl, token).then(() => {
      // TODO
    });
  };
}

function setRecruiterSuccessful(recruiterUsername) {
  return {
    type: 'SET_RECRUITER_SUCCESSFUL',
    recruiterUsername,
    timestamp: Date.now(),
  };
}

function setRecruiter(recruiterUsername) {
  return (dispatch, getState) => {
    const state = getState();
    const { apiUrl, token } = state.auth;
    return userApi.setRecruiter(apiUrl, token, recruiterUsername).then(() => {
      dispatch(setRecruiterSuccessful(recruiterUsername));
    });
  };
}

function fetchUserRankSuccessful(userRank) {
  return {
    type: 'FETCH_USER_RANK_SUCCESSFUL',
    userRank,
    timestamp: Date.now(),
  };
}

function fetchUserRankError(error) {
  return {
    type: 'FETCH_USER_RANK_ERROR',
    error,
    timestamp: Date.now(),
  };
}

function fetchUserRank() {
  return (dispatch, getState) => {
    const { apiUrl, token } = getState().auth;

    if (!apiUrl || !token) {
      return Promise.resolve();
    }

    return userApi
      .fetchUserRank(apiUrl, token)
      .then((userRank) => dispatch(fetchUserRankSuccessful(userRank)))
      .catch((err) => fetchUserRankError(err));
  };
}

function fetchUserRankIfNeeded() {
  return (dispatch, getState) => {
    const {
      apiUrl,
      token,
      user: { rank },
    } = getState().auth;

    if (
      !apiUrl ||
      !token ||
      (rank &&
        rank.lastSuccessfulFetch &&
        rank.lastSuccessfulFetch > Date.now() - MIN_USER_UPDATE)
    ) {
      return Promise.resolve();
    }

    return dispatch(fetchUserRank());
  };
}

function claimMonthlyDiamondBonus() {
  return (dispatch, getState) => {
    const state = getState();
    const { apiUrl, token } = state.auth;
    if (!apiUrl || !token) {
      return Promise.resolve();
    }

    return userApi
      .claimMonthlyDiamondBonus(apiUrl, token)
      .then((responseJson) => {
        if (typeof responseJson.newLootgemBalance === 'number') {
          dispatch(updateLootgemBalance(responseJson));
        }
        if (responseJson.totalSubscriptionDiamondBonus) {
          dispatch(updateTotalSubscriptionDiamondBonus(responseJson));
        }
        return responseJson;
      });
  };
}

function claimMonthlyStoreDiscount() {
  return (dispatch, getState) => {
    const state = getState();
    const { apiUrl, token } = state.auth;
    if (!apiUrl || !token) {
      return Promise.resolve();
    }

    return userApi.claimMonthlyStoreDiscount(apiUrl, token).then((card) => {
      dispatch({
        type: MONTHLY_STORE_DISCOUNT_UPDATED,
        card,
        subscriptionStoreDiscountsToBeClaimed:
          card.subscriptionStoreDiscountsToBeClaimed,
      });
      return card;
    });
  };
}

function transferData(encryptedToken) {
  return (dispatch, getState) => {
    const state = getState();
    const { apiUrl, token } = state.auth;
    if (!apiUrl || !token) {
      return Promise.resolve();
    }

    return userApi
      .transferData(apiUrl, token, { encryptedToken })
      .then((user) => {
        dispatch(updateUserSuccessful(user));
        dispatch(invalidateCards());
        dispatch(invalidatePurchases());
        return user;
      });
  };
}

function setNewFavourites(favouriteLoots) {
  return async (dispatch, getState) => {
    if (favouriteLoots.length >= MAX_NUMBER_FAVOURITES) {
      // todo this should be changed into specific error
      throw new Error('To many favourites');
    }
    const { apiUrl, token } = authSelector(getState());

    return userApi
      .setFavourites(apiUrl, token, favouriteLoots)
      .then(() => dispatch(updateUserData({ favouriteLoots })));
  };
}

function setFavourite(newFavourite) {
  return async (dispatch, getState) => {
    const { favouriteLoots = [] } = userSelector(getState());

    let newFavouriteLoots;
    if (favouriteLoots.includes(newFavourite)) {
      newFavouriteLoots = favouriteLoots.filter(
        (loot) => loot !== newFavourite,
      );
    } else {
      newFavouriteLoots = [...favouriteLoots, newFavourite];
    }

    return dispatch(setNewFavourites(newFavouriteLoots));
  };
}

module.exports = {
  fetchUserIfNeeded,
  fetchUser,
  fetchUserRankIfNeeded,
  invalidateUser,
  updateUser,
  markHintAsSeen,
  updateLootcoinBalance,
  deleteAccount,
  setPassword,
  verifyEmail,
  requestEmailVerification,
  setRecruiter,
  setLanguage,
  claimMonthlyDiamondBonus,
  claimMonthlyStoreDiscount,
  transferData,
  updateTicketBalance,
  updateLootgemBalance,
  updateUserData,
  approveLegalData,
  setNewFavourites,
  setFavourite,
  subscribeToNewsletter,
  readTutorial,
};
